import { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, X } from 'lucide-react'
import FaqList from './FaqList'
import type { Faq } from '@/data/faqs'

const SUGGESTIONS = ['Cost', 'Planning permission', 'Roof pitch', 'Shed vs gable', 'Accuracy']

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length > 1)
}

// Every typed word must appear somewhere in the question or answer.
function matches(item: Faq, tokens: string[]): boolean {
  if (tokens.length === 0) return true
  const haystack = `${item.q} ${item.a}`.toLowerCase()
  return tokens.every(t => haystack.includes(t))
}

function score(item: Faq, tokens: string[]): number {
  const question = item.q.toLowerCase()
  return tokens.reduce((acc, t) => acc + (question.includes(t) ? 2 : 0) + 1, 0)
}

function FaqSearch({ items }: { items: Faq[] }) {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const tokens = useMemo(() => tokenize(query), [query])
  const filtered = useMemo(
    () => items.filter(i => matches(i, tokens)).sort((a, b) => score(b, tokens) - score(a, tokens)),
    [items, tokens],
  )

  const searching = query.trim().length > 0
  const clear = () => {
    setQuery('')
    inputRef.current?.focus()
  }

  return (
    <div>
      <div role="search" className="card p-4 sm:p-5">
        <label htmlFor="faq-search" className="mb-2 block text-sm font-bold text-ink">
          Search the questions
        </label>
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-steel-400"
          />
          <input
            ref={inputRef}
            id="faq-search"
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape' && query) {
                e.preventDefault()
                clear()
              }
            }}
            placeholder="Try “cost”, “pitch” or “permission”"
            autoComplete="off"
            aria-describedby="faq-search-hint"
            className="field-input !pl-11 !pr-11 [&::-webkit-search-cancel-button]:hidden"
          />
          {searching && (
            <button
              type="button"
              onClick={clear}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-steel-500 hover:bg-steel-100 hover:text-ink"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>

        <p id="faq-search-hint" className="mt-2 text-sm text-steel-500">
          {searching
            ? `${filtered.length} of ${items.length} questions match “${query.trim()}”.`
            : `${items.length} questions answered below.`}
        </p>

        <div className="mt-3 flex flex-wrap gap-2">
          {SUGGESTIONS.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setQuery(s)
                inputRef.current?.focus()
              }}
              className="rounded-full border border-steel-300 px-3 py-1 text-sm font-semibold text-steel-600 transition-colors hover:border-blueprint-400 hover:bg-blueprint-50 hover:text-blueprint-800"
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {searching ? `${filtered.length} questions match your search.` : `${items.length} questions shown.`}
      </p>

      <div className="mt-6">
        {filtered.length > 0 ? (
          <FaqList items={filtered} highlight={query} />
        ) : (
          <div className="card p-8 text-center">
            <p className="text-lg font-bold text-ink">No questions match “{query.trim()}”</p>
            <p className="mx-auto mt-2 max-w-sm text-steel-600">
              Try a shorter phrase, or browse the guides for a fuller answer.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <button type="button" onClick={clear} className="btn btn-outline">
                Clear search
              </button>
              <Link to="/blog" className="btn btn-dark no-underline">
                Read the guides
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FaqSearch
